/* Public shell — top nav, mobile drawer, footer. Rebranded to Purple Angels Kids
   Foundation with Girl Power as a co-equal sub-brand entry. */
import { useEffect, useState } from "react";
import { Link, useRoute } from "../../lib/router-compat.jsx";
import { Icon, BrandMark } from "../../ui/index.js";
import { useSiteSettings } from "../../data/hooks.js";

const BRAND_FALLBACK = "Purple Angels Kids Foundation";

const PUBLIC_NAV = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Programs", path: "/programs" },
  { label: "Girl Unlimited", path: "/girl-unlimited" },
  { label: "Events", path: "/events" },
  { label: "Gallery", path: "/gallery" },
  { label: "Blog", path: "/blog" },
  { label: "Contact", path: "/contact" },
];

export default function PublicShell({ children }) {
  const route = useRoute();
  const { settings } = useSiteSettings();
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const brand = settings?.brand || BRAND_FALLBACK;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [route.path]);
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const isActive = (p) => {
    if (p === "/") return route.path === "/";
    return route.path === p || route.path.startsWith(p + "/");
  };

  const c = settings?.contact || {};
  const wa = (c.whatsappNumber || "").replace(/\D/g, "");

  return (
    <div className="public-shell">
      <nav className={`topnav ${scrolled ? "scrolled" : ""}`} aria-label="Primary">
        <div className="topnav__inner container">
          <Link to="/" className="topnav__logo">
            <BrandMark size={36} />
            <span className="topnav__wordmark">{brand}</span>
          </Link>
          <div className="topnav__links topnav__links--desktop">
            {PUBLIC_NAV.map(l => (
              <Link key={l.path} to={l.path} className={`topnav__link ${isActive(l.path) ? "active" : ""}`}>{l.label}</Link>
            ))}
          </div>
          <div className="topnav__cta-row">
            <Link to="/donate" className="btn btn--primary topnav__donate-btn">
              <Icon.Heart size={16} /> <span>Donate</span>
            </Link>
            <button className="topnav__hamburger" aria-label="Open menu" aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)}>
              <Icon.Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {drawerOpen && (
        <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} role="presentation">
          <aside className="public-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Site menu">
            <div className="public-drawer__head">
              <Link to="/" className="topnav__logo" onClick={() => setDrawerOpen(false)}>
                <BrandMark size={32} />
                <span className="topnav__wordmark">{brand}</span>
              </Link>
              <button className="modal__close" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
                <Icon.X size={18} />
              </button>
            </div>
            <div className="public-drawer__nav">
              {PUBLIC_NAV.map(l => (
                <Link key={l.path} to={l.path} className={`public-drawer__link ${isActive(l.path) ? "active" : ""}`}>{l.label}</Link>
              ))}
            </div>
            <div className="public-drawer__foot">
              <Link to="/donate" className="btn btn--primary btn--lg btn--full">
                <Icon.Heart size={16} /> Donate
              </Link>
              <div className="public-drawer__socials">
                {c.instagram && <a href={c.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"><Icon.Instagram size={18} /></a>}
                {c.facebook && <a href={c.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"><Icon.Facebook size={18} /></a>}
                {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" aria-label="WhatsApp"><Icon.MessageCircle size={18} /></a>}
              </div>
            </div>
          </aside>
        </div>
      )}

      <main id="main">{children}</main>
      <PublicFooter settings={settings} brand={brand} />
    </div>
  );
}

function PublicFooter({ settings, brand }) {
  const c = settings?.contact || {};
  const wa = (c.whatsappNumber || "").replace(/\D/g, "");
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div>
            <div className="footer__brand-wordmark">{brand}</div>
            <p className="t-sm muted" style={{ maxWidth: 280, marginTop: 8 }}>
              Dedicated to the holistic well-being of less fortunate children — sick or underprivileged — across Ghana.
            </p>
            <Link to="/donate" className="btn btn--primary" style={{ marginTop: 16 }}>
              <Icon.Heart size={14} /> Support our work
            </Link>
          </div>
          <div>
            <div className="footer__heading">Explore</div>
            <ul className="footer__list">
              <li><Link to="/about">About</Link></li>
              <li><Link to="/programs">Programs</Link></li>
              <li><Link to="/girl-unlimited">Girl Unlimited</Link></li>
              <li><Link to="/events">Events</Link></li>
              <li><Link to="/founder">Founder</Link></li>
              <li><Link to="/gallery">Gallery</Link></li>
              <li><Link to="/blog">Blog</Link></li>
              <li><Link to="/privacy">Privacy</Link></li>
            </ul>
          </div>
          <div>
            <div className="footer__heading">Contact</div>
            <ul className="footer__list">
              {c.publicEmail && <li><a href={`mailto:${c.publicEmail}`}>{c.publicEmail}</a></li>}
              {c.publicPhone && <li><a href={`tel:${c.publicPhone}`}>{c.publicPhone}</a></li>}
              <li><Link to="/contact">Send a message</Link></li>
            </ul>
          </div>
          <div>
            <div className="footer__heading">Follow</div>
            <ul className="footer__list">
              {c.instagram && <li><a href={c.instagram} target="_blank" rel="noreferrer"><Icon.Instagram size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />Instagram</a></li>}
              {c.facebook && <li><a href={c.facebook} target="_blank" rel="noreferrer"><Icon.Facebook size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />Facebook</a></li>}
              {wa && <li><a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer"><Icon.MessageCircle size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />WhatsApp</a></li>}
            </ul>
          </div>
        </div>
        <div className="footer__bottom">
          <div>© {new Date().getFullYear()} {brand}. All rights reserved.</div>
          <div>Made in Accra, Ghana.</div>
        </div>
      </div>
    </footer>
  );
}
